import type { ReactNode } from 'react';
import styles from './Topbar.module.css';

interface TopbarAction {
  label: string;
  onClick: () => void;
  variant?: 'primary' | 'ghost';
}

interface TopbarProps {
  title: string;
  subtitle?: string;
  moduleIcon: ReactNode;
  moduleColor: string;
  moduleBg: string;
  /** Single primary action (legacy). Prefer `actions` for multiple buttons. */
  action?: TopbarAction;
  /** Multiple buttons, rendered right-aligned in declared order. */
  actions?: TopbarAction[];
  /** Optional extra content rendered to the right of title (e.g. status chip). */
  extra?: ReactNode;
  /** Optional node in the right cluster, before the action buttons
      (p.ej. un conmutador de vista Bandeja/Mapa). */
  rightSlot?: ReactNode;
}

/**
 * Cinta de cabecera del módulo. Banda en el tinte suave del módulo con un
 * subrayado de 3px en su acento (una de las cuatro apariciones del color de
 * módulo). Icono en chip blanco, titular en serif. Va justo bajo la barra de
 * navegación (tinta) y sobre la fila de filtros. Idéntica en los diez módulos.
 */
export function Topbar({
  title,
  subtitle,
  moduleIcon,
  moduleColor,
  moduleBg,
  action,
  actions,
  extra,
  rightSlot,
}: TopbarProps) {
  const allActions: TopbarAction[] = [
    ...(actions ?? []),
    ...(action ? [{ ...action, variant: action.variant ?? 'primary' }] : []),
  ];
  return (
    <div className={styles.topbar}>
      <div className={styles.left}>
        <div
          className={styles.chip}
          style={{ background: moduleBg, borderColor: `color-mix(in srgb, ${moduleColor} 42%, transparent)` }}
        >
          {moduleIcon}
        </div>
        <div className={styles.info}>
          <div className={styles.title} style={{ color: moduleColor }}>{title}</div>
          {subtitle && <div className={styles.subtitle}>{subtitle}</div>}
        </div>
        {extra && <div className={styles.extra}>{extra}</div>}
      </div>
      {(allActions.length > 0 || rightSlot) && (
        <div className={styles.right}>
          {rightSlot}
          {allActions.map((a, i) => (
            <button
              key={i}
              className={`${styles.btn} ${a.variant === 'ghost' ? styles.ghost : styles.primary}`}
              onClick={a.onClick}
            >
              {a.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
